import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, FileText, BookOpen as BookIcon, Download, X, AlertCircle, Users as UsersIcon, ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

// ============================================================
// BOOKS PANEL — nonprofit accounting (Chart of Accounts, Journal,
// General Ledger, Trial Balance, P&L, Balance Sheet, Functional Expense,
// unified Donors & Customers)
// ============================================================

const SUB_TABS = [
    { id: "coa", label: "Chart of Accounts" },
    { id: "journal", label: "Journal" },
    { id: "reconcile", label: "Reconcile Cash" },
    { id: "cash-summary", label: "Cash Support Summary" },
    { id: "gl", label: "General Ledger" },
    { id: "trial", label: "Trial Balance" },
    { id: "pl", label: "P&L" },
    { id: "bs", label: "Balance Sheet" },
    { id: "fx", label: "Functional Expense" },
    { id: "donors", label: "Donors" },
    { id: "customers", label: "Customers" },
    { id: "audit", label: "Audit & Reconcile" },
];

export const BooksPanel = () => {
    const [sub, setSub] = useState("coa");
    return (
        <div data-testid="admin-books-panel">
            <div className="flex justify-between items-end flex-wrap gap-3 mb-6">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Books</h2>
                    <p className="text-sm text-[#4A5568] mt-1">Double-entry accounting · auto-posted from orders, donations &amp; spend payouts</p>
                </div>
                <div className="text-xs text-[#4A5568] bg-[#FDFBF7] border border-black/5 rounded-full px-3 py-1.5">
                    <BookIcon className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5 text-[#E53935]" strokeWidth={1.5} />
                    Modeled on ERPNext Non-Profit
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6 border-b border-black/10" data-testid="admin-books-subtabs">
                {SUB_TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setSub(t.id)}
                        data-testid={`books-subtab-${t.id}`}
                        className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-all ${sub === t.id ? "border-[#E53935] text-[#E53935]" : "border-transparent text-[#4A5568] hover:text-[#1C252E]"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {sub === "coa" && <ChartOfAccounts />}
            {sub === "journal" && <Journal />}
            {sub === "reconcile" && <ReconcileCash />}
            {sub === "cash-summary" && <CashSupportSummary />}
            {sub === "gl" && <GeneralLedger />}
            {sub === "trial" && <TrialBalance />}
            {sub === "pl" && <ProfitLoss />}
            {sub === "bs" && <BalanceSheet />}
            {sub === "fx" && <FunctionalExpense />}
            {sub === "donors" && <Donors />}
            {sub === "customers" && <Customers />}
            {sub === "audit" && <AuditReconcile />}
        </div>
    );
};

// -------------------- Chart of Accounts --------------------
const TYPE_COLOR = {
    Asset: "bg-blue-50 text-blue-700",
    Liability: "bg-amber-50 text-amber-700",
    Equity: "bg-purple-50 text-purple-700",
    Income: "bg-green-50 text-green-700",
    Expense: "bg-red-50 text-red-700",
};

const ChartOfAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [show, setShow] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", type: "Asset", functional: "", description: "" });

    const load = () => api.get("/books/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ code: "", name: "", type: "Asset", functional: "", description: "" });
        setShow(true);
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            code: a.code,
            name: a.name,
            type: a.type,
            functional: a.functional || "",
            description: a.description || "",
        });
        setShow(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.patch(`/books/accounts/${editing.id}`, {
                    name: form.name.trim(),
                    functional: form.functional || null,
                    description: form.description.trim() || null,
                });
                toast.success(`Renamed ${editing.code}`);
            } else {
                await api.post("/books/accounts", {
                    code: form.code.trim(),
                    name: form.name.trim(),
                    type: form.type,
                    functional: form.functional || null,
                    description: form.description.trim() || null,
                });
                toast.success("Account created");
            }
            setShow(false);
            setEditing(null);
            setForm({ code: "", name: "", type: "Asset", functional: "", description: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Delete account ${a.code} — ${a.name}?`)) return;
        try {
            await api.delete(`/books/accounts/${a.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="books-coa">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#4A5568]">{accounts.length} accounts · system accounts are pre-seeded and cannot be deleted (mark inactive instead)</p>
                <button onClick={openCreate} data-testid="books-add-account-btn" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-5 py-2 text-sm font-medium">
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add account
                </button>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="books-coa-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Code</th>
                            <th className="text-left px-4 py-3 font-medium">Name</th>
                            <th className="text-left px-4 py-3 font-medium">Type</th>
                            <th className="text-left px-4 py-3 font-medium">Functional</th>
                            <th className="text-center px-4 py-3 font-medium">System</th>
                            <th className="text-right px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {accounts.map((a) => (
                            <tr key={a.id} data-testid={`books-account-row-${a.code}`}>
                                <td className="px-4 py-3 font-mono text-xs text-[#1C252E]">{a.code}</td>
                                <td className="px-4 py-3 text-[#1C252E]">{a.name}</td>
                                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${TYPE_COLOR[a.type] || "bg-gray-100 text-gray-700"}`}>{a.type}</span></td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{a.functional || "—"}</td>
                                <td className="px-4 py-3 text-center text-xs text-[#4A5568]">{a.system ? "✓" : "—"}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => openEdit(a)} data-testid={`books-account-edit-${a.code}`} className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#4A5568] hover:text-[#E53935]" aria-label="Rename">
                                        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    {!a.system && (
                                        <button onClick={() => remove(a)} data-testid={`books-account-delete-${a.code}`} className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]" aria-label="Delete">
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setShow(false); setEditing(null); }} data-testid="books-account-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium">{editing ? `Rename ${editing.code}` : "New account"}</h3>
                            <button onClick={() => { setShow(false); setEditing(null); }} className="p-2"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Code {editing ? "" : <span className="text-[#E53935]">*</span>}</label>
                                    <input required={!editing} disabled={!!editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. 5070" className="w-full rounded-xl border border-black/10 px-4 py-3 font-mono disabled:bg-[#F4F1EA]" data-testid="books-account-code" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Type {editing ? "" : <span className="text-[#E53935]">*</span>}</label>
                                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} disabled={!!editing} className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white disabled:bg-[#F4F1EA]" data-testid="books-account-type">
                                        {["Asset", "Liability", "Equity", "Income", "Expense"].map((t) => <option key={t}>{t}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Name <span className="text-[#E53935]">*</span></label>
                                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Payment Processor Clearing" className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="books-account-name" />
                            </div>
                            {form.type === "Expense" && (
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Functional category</label>
                                    <select value={form.functional} onChange={(e) => setForm({ ...form, functional: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white" data-testid="books-account-functional">
                                        <option value="">— None —</option>
                                        <option>Program</option>
                                        <option>Management</option>
                                        <option>Fundraising</option>
                                    </select>
                                    <p className="text-[11px] text-[#4A5568] mt-1.5">Used for the Form 990 functional expense report.</p>
                                </div>
                            )}
                            <button type="submit" data-testid="books-account-save" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3 font-medium">{editing ? "Save changes" : "Create account"}</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// -------------------- Reconcile Cash --------------------
// Purpose: give admin one-click ways to log the cash outflows / payouts
// that the automated JEs never captured (Alibaba buys, ShipStation
// top-ups, Stripe fees, PayPal payouts). Every button posts a proper
// balanced JE so the trial balance always stays clean.
const ReconcileCash = () => {
    const [accounts, setAccounts] = useState([]);
    const [outflow, setOutflow] = useState({
        date: new Date().toISOString().slice(0, 10),
        amount: "",
        expense_code: "1100",
        source_account: "1010",
        memo: "",
    });
    const [payout, setPayout] = useState({
        processor: "stripe",
        payout_date: new Date().toISOString().slice(0, 10),
        gross_amount: "",
        fee_amount: "",
        payout_reference: "",
    });

    useEffect(() => {
        api.get("/books/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    }, []);

    const submitOutflow = async (e) => {
        e.preventDefault();
        const amt = parseFloat(outflow.amount);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Amount must be > 0"); return; }
        if (!outflow.memo.trim()) { toast.error("Memo required"); return; }
        try {
            await api.post("/books/cash-outflow", { ...outflow, amount: amt });
            toast.success(`Logged $${amt.toFixed(2)} outflow · books balanced`);
            setOutflow({ ...outflow, amount: "", memo: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const submitPayout = async (e) => {
        e.preventDefault();
        const gross = parseFloat(payout.gross_amount);
        const fee = parseFloat(payout.fee_amount || "0");
        if (!Number.isFinite(gross) || gross <= 0) { toast.error("Gross amount must be > 0"); return; }
        if (fee > gross) { toast.error("Fees can't exceed gross"); return; }
        try {
            const { data } = await api.post("/books/processor-payout", { ...payout, gross_amount: gross, fee_amount: fee });
            toast.success(`Payout logged · $${data.net_to_bank.toFixed(2)} to bank`);
            setPayout({ ...payout, gross_amount: "", fee_amount: "", payout_reference: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const cashAccounts = accounts.filter((a) => a.type === "Asset" && ["1010", "1020", "1060"].includes(a.code));
    const expenseTargets = accounts.filter((a) => ["1100", "1150", "1200", "1210", "5010", "5020", "5030", "5040", "5050", "5060", "5070", "1090"].includes(a.code));

    return (
        <div data-testid="books-reconcile" className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                <p className="font-semibold mb-1">Use this to close the bank/ledger gap</p>
                <p className="text-xs">Log every real cash outflow (Alibaba, ShipStation, direct fees) and every Stripe/PayPal payout so 1010 Cash & Bank matches the bank statement. Online sales now post to 1020 Processor Clearing first — payouts sweep them into 1010 minus fees.</p>
            </div>

            {/* --- Cash outflow --- */}
            <form onSubmit={submitOutflow} className="bg-white border border-black/5 rounded-2xl p-6 space-y-4" data-testid="reconcile-cash-outflow-form">
                <div>
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">Log a cash outflow</h3>
                    <p className="text-xs text-[#4A5568] mt-1">Money that left the bank — inventory purchase, postage, direct-charged fees, supplies, etc.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Date</label>
                        <input required type="date" value={outflow.date} onChange={(e) => setOutflow({ ...outflow, date: e.target.value })} className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Amount (USD)</label>
                        <input required type="number" step="0.01" min="0.01" value={outflow.amount} onChange={(e) => setOutflow({ ...outflow, amount: e.target.value })} placeholder="679.74" className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm text-right font-mono" data-testid="reconcile-outflow-amount" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Where the money went (debit)</label>
                        <select value={outflow.expense_code} onChange={(e) => setOutflow({ ...outflow, expense_code: e.target.value })} className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm bg-white" data-testid="reconcile-outflow-expense">
                            {expenseTargets.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Paid from (credit)</label>
                        <select value={outflow.source_account} onChange={(e) => setOutflow({ ...outflow, source_account: e.target.value })} className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm bg-white" data-testid="reconcile-outflow-source">
                            {cashAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Memo</label>
                    <input required value={outflow.memo} onChange={(e) => setOutflow({ ...outflow, memo: e.target.value })} placeholder="Alibaba inventory purchase — Q3 sticker restock" className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid="reconcile-outflow-memo" />
                </div>
                <button type="submit" data-testid="reconcile-outflow-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium">Log outflow</button>
            </form>

            {/* --- Processor payout --- */}
            <form onSubmit={submitPayout} className="bg-white border border-black/5 rounded-2xl p-6 space-y-4" data-testid="reconcile-processor-payout-form">
                <div>
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">Log a Stripe / PayPal payout</h3>
                    <p className="text-xs text-[#4A5568] mt-1">Sweeps clearing (1020) into bank (1010) minus fees. Copy the gross + fee numbers directly from the Stripe or PayPal dashboard for that payout.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Processor</label>
                        <select value={payout.processor} onChange={(e) => setPayout({ ...payout, processor: e.target.value })} className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm bg-white" data-testid="reconcile-payout-processor">
                            <option value="stripe">Stripe</option>
                            <option value="paypal">PayPal</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Payout date</label>
                        <input required type="date" value={payout.payout_date} onChange={(e) => setPayout({ ...payout, payout_date: e.target.value })} className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Gross (before fees)</label>
                        <input required type="number" step="0.01" min="0.01" value={payout.gross_amount} onChange={(e) => setPayout({ ...payout, gross_amount: e.target.value })} placeholder="1000.00" className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm text-right font-mono" data-testid="reconcile-payout-gross" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Fees deducted</label>
                        <input type="number" step="0.01" min="0" value={payout.fee_amount} onChange={(e) => setPayout({ ...payout, fee_amount: e.target.value })} placeholder="32.30" className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm text-right font-mono" data-testid="reconcile-payout-fee" />
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">Reference (optional)</label>
                    <input value={payout.payout_reference} onChange={(e) => setPayout({ ...payout, payout_reference: e.target.value })} placeholder="po_1PxYz... from Stripe dashboard" className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm" />
                </div>
                <div className="text-xs text-[#4A5568]">
                    Net to bank = <strong className="text-[#1C252E] font-mono">${((parseFloat(payout.gross_amount) || 0) - (parseFloat(payout.fee_amount) || 0)).toFixed(2)}</strong>
                </div>
                <button type="submit" data-testid="reconcile-payout-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium">Log payout</button>
            </form>
        </div>
    );
};

// -------------------- Cash Support Summary --------------------
// Waterfall report per PDF spec: Gross → COGS → Shipping → Fees → Net,
// then fund allocations (internal split of net margin, NOT extra cash).
const CashSupportSummary = () => {
    const [data, setData] = useState(null);
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");

    const load = () => {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        api.get("/books/reports/cash-support-summary", { params })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load summary"));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div data-testid="books-cash-summary" className="space-y-6">
            <div className="bg-white border border-black/5 rounded-2xl p-6">
                <div className="flex flex-wrap items-end gap-3 mb-5">
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">From</label>
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid="cash-summary-start" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">To</label>
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid="cash-summary-end" />
                    </div>
                    <button onClick={load} data-testid="cash-summary-refresh" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium">Refresh</button>
                    <button onClick={() => { setStart(""); setEnd(""); setTimeout(load, 0); }} className="text-sm text-[#4A5568] hover:text-[#E53935] underline">All time</button>
                </div>
                {!data ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : (
                    <>
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-4">Waterfall</h3>
                        <table className="w-full text-sm mb-6">
                            <tbody>
                                {data.waterfall.map((row, i) => {
                                    const isDeduction = row.type === "deduction";
                                    const isSubtotal = row.type === "subtotal";
                                    const isInfo = row.type === "info";
                                    const isGross = row.type === "gross";
                                    return (
                                        <tr key={i} className={`border-b border-black/5 ${isSubtotal ? "bg-[#F4F1EA]" : isInfo ? "text-xs" : ""}`} data-testid={`waterfall-row-${i}`}>
                                            <td className={`py-2.5 pr-4 ${isGross || isSubtotal ? "font-semibold text-[#1C252E]" : isInfo ? "text-[#4A5568]" : "text-[#1C252E]"}`}>{row.label}</td>
                                            <td className={`py-2.5 text-right font-mono ${isDeduction ? "text-[#E53935]" : isSubtotal ? "font-bold text-[#1C252E]" : isInfo ? "text-[#4A5568]" : "text-[#1C252E]"}`}>
                                                {isDeduction ? `− ${fmt(row.amount)}` : fmt(row.amount)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-4">Fund allocations <span className="text-xs font-normal text-[#4A5568] ml-2">(internal split of net margin — not additional cash)</span></h3>
                        <table className="w-full text-sm mb-6">
                            <tbody>
                                {data.fund_allocations.map((row, i) => (
                                    <tr key={i} className={`border-b border-black/5 ${row.bold ? "bg-[#F4F1EA]" : ""}`} data-testid={`fund-row-${i}`}>
                                        <td className={`py-2.5 pr-4 ${row.bold ? "font-bold text-[#1C252E]" : "text-[#1C252E]"}`}>{row.label}</td>
                                        <td className={`py-2.5 text-right font-mono ${row.bold ? "font-bold text-[#1C252E]" : "text-[#1C252E]"}`}>{fmt(row.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {data.cash_position && (
                            <>
                                <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-4">Where the cash sits right now <span className="text-xs font-normal text-[#4A5568] ml-2">(cash-on-hand snapshot)</span></h3>
                                <table className="w-full text-sm mb-4">
                                    <tbody>
                                        {data.cash_position.map((row, i) => (
                                            <tr key={i} className={`border-b border-black/5 ${row.bold ? "bg-[#F4F1EA]" : ""}`} data-testid={`cash-row-${i}`}>
                                                <td className={`py-2.5 pr-4 ${row.bold ? "font-bold text-[#1C252E]" : "text-[#1C252E]"}`}>{row.label}</td>
                                                <td className={`py-2.5 text-right font-mono ${row.bold ? "font-bold text-[#1C252E]" : "text-[#1C252E]"}`}>{fmt(row.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        <div className="mt-6 space-y-1">
                            {data.notes.map((n, i) => (
                                <p key={i} className="text-xs text-[#4A5568]">• {n}</p>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// -------------------- Journal --------------------
const emptyForm = () => ({ entry_date: new Date().toISOString().slice(0, 10), narration: "", lines: [{ account_code: "", debit: "", credit: "", description: "" }, { account_code: "", debit: "", credit: "", description: "" }] });

const Journal = () => {
    const [entries, setEntries] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [show, setShow] = useState(false);
    const [editing, setEditing] = useState(null);   // JE row being edited (null when creating)
    const [editReason, setEditReason] = useState("");
    const [form, setForm] = useState(emptyForm());

    const load = () => api.get("/books/journal").then((r) => setEntries(r.data)).catch(() => {});
    useEffect(() => {
        load();
        api.get("/books/accounts").then((r) => setAccounts(r.data));
    }, []);

    const totalD = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const totalC = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    const balanced = Math.abs(totalD - totalC) < 0.01 && totalD > 0;

    const updateLine = (idx, field, val) => {
        const lines = [...form.lines];
        lines[idx] = { ...lines[idx], [field]: val };
        if (field === "debit" && val) lines[idx].credit = "";
        if (field === "credit" && val) lines[idx].debit = "";
        setForm({ ...form, lines });
    };

    const addLine = () => setForm({ ...form, lines: [...form.lines, { account_code: "", debit: "", credit: "", description: "" }] });
    const removeLine = (idx) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });

    const openCreate = () => {
        setEditing(null);
        setEditReason("");
        setForm(emptyForm());
        setShow(true);
    };

    const openEdit = (je) => {
        if (je.source_type && je.source_type !== "manual") {
            toast.error(`Auto-posted from ${je.source_type} — edit the source record or void this JE and repost.`);
            return;
        }
        setEditing(je);
        setEditReason("");
        setForm({
            entry_date: je.entry_date,
            narration: je.narration,
            lines: (je.lines || []).map((l) => ({
                account_code: l.account_code,
                debit: l.debit > 0 ? String(l.debit) : "",
                credit: l.credit > 0 ? String(l.credit) : "",
                description: l.description || "",
            })),
        });
        setShow(true);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!balanced) {
            toast.error("Debits must equal credits and be greater than zero");
            return;
        }
        try {
            const lines = form.lines
                .filter((l) => l.account_code && (l.debit || l.credit))
                .map((l) => ({ account_code: l.account_code, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0, description: l.description || null }));
            if (editing) {
                await api.patch(`/books/journal/${editing.id}`, {
                    entry_date: form.entry_date,
                    narration: form.narration,
                    lines,
                    edit_reason: editReason.trim() || null,
                });
                toast.success(`Entry ${editing.entry_number} updated`);
            } else {
                await api.post("/books/journal", { entry_date: form.entry_date, narration: form.narration, lines });
                toast.success("Journal entry posted");
            }
            setShow(false);
            setEditing(null);
            setForm(emptyForm());
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const voidEntry = async (je) => {
        if (!window.confirm(`Void JE ${je.entry_number}? It stays in the DB but is excluded from reports and balances.`)) return;
        try {
            await api.delete(`/books/journal/${je.id}`);
            toast.success(`Voided ${je.entry_number}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Void failed");
        }
    };

    const hardDelete = async (je) => {
        if ((je.source_type || "manual") !== "manual") {
            toast.error(`Auto-posted from ${je.source_type} — cannot hard delete. Use Void instead.`);
            return;
        }
        if (!window.confirm(`PERMANENTLY DELETE JE ${je.entry_number}?\n\nThis cannot be undone. The entry will be removed from the DB entirely — no audit trail. Only proceed for entries you never should have posted.`)) return;
        try {
            await api.delete(`/books/journal/${je.id}`, { params: { hard: true } });
            toast.success(`Deleted ${je.entry_number}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="books-journal">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#4A5568]">{entries.length} entries · sorted by post date</p>
                <button onClick={openCreate} data-testid="books-add-journal-btn" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-5 py-2 text-sm font-medium">
                    <Plus className="w-4 h-4" strokeWidth={2} /> Manual entry
                </button>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto" data-testid="books-journal-table">
                <table className="w-full text-sm">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Entry #</th>
                            <th className="text-left px-4 py-3 font-medium">Date</th>
                            <th className="text-left px-4 py-3 font-medium">Narration</th>
                            <th className="text-left px-4 py-3 font-medium">Source</th>
                            <th className="text-right px-4 py-3 font-medium">Debit</th>
                            <th className="text-right px-4 py-3 font-medium">Credit</th>
                            <th className="text-right px-4 py-3 font-medium">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {entries.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-[#4A5568]">No journal entries yet. They post automatically as orders, donations, and spend payouts happen.</td></tr>}
                        {entries.map((e) => {
                            const isManual = !e.source_type || e.source_type === "manual";
                            return (
                                <React.Fragment key={e.id}>
                                    <tr data-testid={`books-je-row-${e.entry_number}`} className="bg-white">
                                        <td className="px-4 py-3 font-mono text-xs text-[#1C252E] font-medium">
                                            {e.entry_number}
                                            {(e.revisions || []).length > 0 && (
                                                <span title={`${e.revisions.length} edit(s)`} className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 font-bold">edited</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-[#4A5568]">{e.entry_date}</td>
                                        <td className="px-4 py-3 text-[#1C252E]">{e.narration}</td>
                                        <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#F4F1EA] text-[#4A5568]">{e.source_type}</span></td>
                                        <td className="px-4 py-3 text-right font-mono text-[#1C252E]">${e.total_debit.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-[#1C252E]">${e.total_credit.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => openEdit(e)}
                                                disabled={!isManual}
                                                title={isManual ? "Edit this entry" : `Auto-posted from ${e.source_type} — edit the source record instead`}
                                                data-testid={`books-je-edit-${e.entry_number}`}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1C252E] hover:text-[#E53935] disabled:text-[#CBD5E1] disabled:cursor-not-allowed px-2"
                                            >
                                                <Edit2 className="w-3.5 h-3.5" strokeWidth={2} /> Edit
                                            </button>
                                            <button
                                                onClick={() => voidEntry(e)}
                                                title="Soft void — keeps the row for audit, excludes from reports"
                                                data-testid={`books-je-void-${e.entry_number}`}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#4A5568] hover:text-amber-700 px-2"
                                            >
                                                Void
                                            </button>
                                            <button
                                                onClick={() => hardDelete(e)}
                                                disabled={!isManual}
                                                title={isManual ? "Permanently delete (no audit trail)" : `Auto-posted from ${e.source_type} — can't hard delete`}
                                                data-testid={`books-je-delete-${e.entry_number}`}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#E53935] hover:text-[#B71C1C] disabled:text-[#CBD5E1] disabled:cursor-not-allowed px-2"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                    {(e.lines || []).map((ln, i) => (
                                        <tr key={`${e.id}-${i}`} className="bg-[#FDFBF7]/40 text-xs">
                                            <td></td>
                                            <td></td>
                                            <td className="px-4 py-2 text-[#4A5568]" colSpan={2}><span className="font-mono">{ln.account_code}</span> · {ln.description || "—"}</td>
                                            <td className="px-4 py-2 text-right font-mono text-[#4A5568]">{ln.debit > 0 ? `$${ln.debit.toFixed(2)}` : ""}</td>
                                            <td className="px-4 py-2 text-right font-mono text-[#4A5568]">{ln.credit > 0 ? `$${ln.credit.toFixed(2)}` : ""}</td>
                                            <td></td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setShow(false); setEditing(null); }} data-testid="books-journal-modal">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="px-8 py-5 border-b border-black/5 flex justify-between items-center">
                            <div>
                                <h3 className="font-heading text-2xl text-[#1C252E] font-medium">{editing ? `Edit ${editing.entry_number}` : "Manual journal entry"}</h3>
                                {editing && (editing.revisions || []).length > 0 && (
                                    <p className="text-xs text-amber-800 mt-1">Previously edited {editing.revisions.length} time(s). Previous versions are preserved on the record.</p>
                                )}
                            </div>
                            <button onClick={() => { setShow(false); setEditing(null); }} className="p-2"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="overflow-y-auto px-8 py-6 space-y-5 flex-1">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Entry date <span className="text-[#E53935]">*</span></label>
                                    <input required type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="books-je-date" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Narration <span className="text-[#E53935]">*</span></label>
                                    <input required value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} placeholder="e.g. Bank fee for May" className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="books-je-narration" />
                                </div>
                            </div>

                            <div className="border-t border-black/5 pt-4">
                                <div className="flex justify-between items-center mb-3">
                                    <p className="text-xs uppercase tracking-wider font-bold text-[#1C252E]">Lines</p>
                                    <p className="text-xs text-[#4A5568]">Each line is debit OR credit. Total must balance.</p>
                                </div>
                                <div className="space-y-2" data-testid="books-je-lines">
                                    {form.lines.map((ln, idx) => (
                                        <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`books-je-line-${idx}`}>
                                            <select required value={ln.account_code} onChange={(e) => updateLine(idx, "account_code", e.target.value)} className="col-span-4 rounded-lg border border-black/10 px-3 py-2 text-sm bg-white" data-testid={`books-je-line-${idx}-account`}>
                                                <option value="">— Account —</option>
                                                {accounts.map((a) => <option key={a.id} value={a.code}>{a.code} · {a.name}</option>)}
                                            </select>
                                            <input type="number" step="0.01" min="0" value={ln.debit} onChange={(e) => updateLine(idx, "debit", e.target.value)} placeholder="Debit" className="col-span-2 rounded-lg border border-black/10 px-3 py-2 text-sm text-right font-mono" data-testid={`books-je-line-${idx}-debit`} />
                                            <input type="number" step="0.01" min="0" value={ln.credit} onChange={(e) => updateLine(idx, "credit", e.target.value)} placeholder="Credit" className="col-span-2 rounded-lg border border-black/10 px-3 py-2 text-sm text-right font-mono" data-testid={`books-je-line-${idx}-credit`} />
                                            <input value={ln.description} onChange={(e) => updateLine(idx, "description", e.target.value)} placeholder="Note" className="col-span-3 rounded-lg border border-black/10 px-3 py-2 text-sm" />
                                            <button type="button" onClick={() => removeLine(idx)} disabled={form.lines.length <= 2} className="col-span-1 p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935] disabled:opacity-30">
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" onClick={addLine} className="mt-3 text-xs text-[#E53935] font-medium hover:underline" data-testid="books-je-add-line">+ Add line</button>
                            </div>

                            {editing && (
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Reason for edit</label>
                                    <input value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="e.g. Corrected transposed digits — original from receipt" className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="books-je-edit-reason" />
                                    <p className="text-[10px] text-[#4A5568] mt-1">Saved on the revision log — helps at audit time.</p>
                                </div>
                            )}

                            <div className={`rounded-xl px-4 py-3 text-xs flex items-center gap-2 ${balanced ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`} data-testid="books-je-balance-readout">
                                <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                                <span>Total debit ${totalD.toFixed(2)} · Total credit ${totalC.toFixed(2)} · {balanced ? "Balanced ✓" : `Out of balance by $${Math.abs(totalD - totalC).toFixed(2)}`}</span>
                            </div>
                        </form>
                        <div className="px-8 py-4 border-t border-black/5 bg-[#FDFBF7] flex justify-end gap-3">
                            <button type="button" onClick={() => { setShow(false); setEditing(null); }} className="px-5 py-2 text-sm font-medium text-[#4A5568]">Cancel</button>
                            <button type="submit" onClick={save} disabled={!balanced} data-testid="books-je-submit" className="bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-50 rounded-full px-6 py-2 text-sm font-medium">{editing ? "Save changes" : "Post entry"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// -------------------- Reports: shared date filter --------------------
const DateRange = ({ start, end, setStart, setEnd, asOf, setAsOf, oneDate = false, testid = "date-range" }) => (
    <div className="flex flex-wrap items-end gap-3 mb-5" data-testid={testid}>
        {oneDate ? (
            <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">As of</label>
                <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid={`${testid}-as-of`} />
            </div>
        ) : (
            <>
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">From</label>
                    <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid={`${testid}-start`} />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">To</label>
                    <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid={`${testid}-end`} />
                </div>
            </>
        )}
    </div>
);

// -------------------- General Ledger --------------------
const GeneralLedger = () => {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [accountCode, setAccountCode] = useState("");
    const [accounts, setAccounts] = useState([]);
    const [data, setData] = useState(null);

    useEffect(() => { api.get("/books/accounts").then((r) => setAccounts(r.data)); }, []);
    useEffect(() => {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        if (accountCode) params.account_code = accountCode;
        api.get("/books/reports/general-ledger", { params }).then((r) => setData(r.data)).catch(() => {});
    }, [start, end, accountCode]);

    return (
        <div data-testid="books-gl">
            <div className="flex flex-wrap items-end gap-3 mb-5">
                <DateRange start={start} end={end} setStart={setStart} setEnd={setEnd} testid="gl-range" />
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Account</label>
                    <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white" data-testid="gl-account-filter">
                        <option value="">— All accounts —</option>
                        {accounts.map((a) => <option key={a.id} value={a.code}>{a.code} · {a.name}</option>)}
                    </select>
                </div>
            </div>

            {data && data.accounts.length === 0 && <p className="text-sm text-[#4A5568]">No transactions in this range.</p>}
            {data && data.accounts.map((a) => (
                <div key={a.account.code} className="bg-white border border-black/5 rounded-2xl mb-4 overflow-hidden" data-testid={`gl-account-${a.account.code}`}>
                    <div className="px-5 py-3 bg-[#F4F1EA] flex justify-between items-center">
                        <p className="font-medium text-[#1C252E]"><span className="font-mono text-xs text-[#4A5568] mr-2">{a.account.code}</span>{a.account.name} <span className="text-xs text-[#4A5568] ml-2">({a.account.type})</span></p>
                        <p className="font-mono text-sm text-[#1C252E]">Closing: <strong>${a.closing_balance.toFixed(2)}</strong></p>
                    </div>
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-[#4A5568]">
                            <tr><th className="text-left px-5 py-2 font-medium">Date</th><th className="text-left px-2 py-2 font-medium">Entry #</th><th className="text-left px-2 py-2 font-medium">Description</th><th className="text-right px-2 py-2 font-medium">Debit</th><th className="text-right px-2 py-2 font-medium">Credit</th><th className="text-right px-5 py-2 font-medium">Running</th></tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {a.rows.map((r, i) => (
                                <tr key={i}>
                                    <td className="px-5 py-2 text-[#4A5568]">{r.entry_date}</td>
                                    <td className="px-2 py-2 font-mono text-[#4A5568]">{r.entry_number}</td>
                                    <td className="px-2 py-2 text-[#1C252E]">{r.description || r.narration}</td>
                                    <td className="px-2 py-2 text-right font-mono">{r.debit > 0 ? `$${r.debit.toFixed(2)}` : ""}</td>
                                    <td className="px-2 py-2 text-right font-mono">{r.credit > 0 ? `$${r.credit.toFixed(2)}` : ""}</td>
                                    <td className="px-5 py-2 text-right font-mono font-medium">${r.running_balance.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            ))}
        </div>
    );
};

// -------------------- Trial Balance --------------------
const TrialBalance = () => {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [data, setData] = useState(null);
    useEffect(() => {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        api.get("/books/reports/trial-balance", { params }).then((r) => setData(r.data));
    }, [start, end]);

    return (
        <div data-testid="books-trial-balance">
            <DateRange start={start} end={end} setStart={setStart} setEnd={setEnd} testid="tb-range" />
            {data && (
                <>
                    <div className={`mb-4 rounded-xl p-3 text-xs ${data.balanced ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                        Total debit <strong>${data.total_debit.toFixed(2)}</strong> · Total credit <strong>${data.total_credit.toFixed(2)}</strong> · {data.balanced ? "Balanced ✓" : "Out of balance — investigate!"}
                    </div>
                    <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                                <tr><th className="text-left px-4 py-3 font-medium">Code</th><th className="text-left px-4 py-3 font-medium">Name</th><th className="text-left px-4 py-3 font-medium">Type</th><th className="text-right px-4 py-3 font-medium">Debit</th><th className="text-right px-4 py-3 font-medium">Credit</th><th className="text-right px-4 py-3 font-medium">Balance</th></tr>
                            </thead>
                            <tbody className="divide-y divide-black/5">
                                {data.rows.map((r) => (
                                    <tr key={r.code} data-testid={`tb-row-${r.code}`}>
                                        <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                                        <td className="px-4 py-3 text-[#1C252E]">{r.name}</td>
                                        <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${TYPE_COLOR[r.type]}`}>{r.type}</span></td>
                                        <td className="px-4 py-3 text-right font-mono text-[#4A5568]">${r.debit.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-[#4A5568]">${r.credit.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right font-mono font-medium text-[#1C252E]">${r.balance.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

// -------------------- P&L --------------------
const ProfitLoss = () => {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [data, setData] = useState(null);
    useEffect(() => {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        api.get("/books/reports/profit-loss", { params }).then((r) => setData(r.data));
    }, [start, end]);
    return (
        <div data-testid="books-pl">
            <DateRange start={start} end={end} setStart={setStart} setEnd={setEnd} testid="pl-range" />
            {data && (
                <div className="space-y-4">
                    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                        <div className="px-5 py-3 bg-green-50 border-b border-green-100"><p className="text-xs uppercase tracking-wider font-bold text-green-800">Income</p></div>
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-black/5">
                                {data.income.map((r) => (
                                    <tr key={r.code} data-testid={`pl-income-${r.code}`}>
                                        <td className="px-4 py-3 font-mono text-xs text-[#4A5568] w-20">{r.code}</td>
                                        <td className="px-4 py-3 text-[#1C252E]">{r.name}{r.restricted && <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">Restricted</span>}</td>
                                        <td className="px-4 py-3 text-right font-mono font-medium">${r.balance.toFixed(2)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-[#F4F1EA]"><td className="px-4 py-3" colSpan={2}><strong>Total Income</strong></td><td className="px-4 py-3 text-right font-mono font-bold" data-testid="pl-total-income">${data.total_income.toFixed(2)}</td></tr>
                                <tr className="bg-[#FDFBF7]"><td className="px-4 py-2 text-xs text-[#4A5568]" colSpan={2}>— Unrestricted</td><td className="px-4 py-2 text-right font-mono text-xs">${data.unrestricted_income.toFixed(2)}</td></tr>
                                <tr className="bg-[#FDFBF7]"><td className="px-4 py-2 text-xs text-[#4A5568]" colSpan={2}>— Restricted</td><td className="px-4 py-2 text-right font-mono text-xs">${data.restricted_income.toFixed(2)}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                        <div className="px-5 py-3 bg-red-50 border-b border-red-100"><p className="text-xs uppercase tracking-wider font-bold text-red-800">Expense</p></div>
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-black/5">
                                {data.expense.map((r) => (
                                    <tr key={r.code} data-testid={`pl-expense-${r.code}`}>
                                        <td className="px-4 py-3 font-mono text-xs text-[#4A5568] w-20">{r.code}</td>
                                        <td className="px-4 py-3 text-[#1C252E]">{r.name}{r.functional && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">{r.functional}</span>}</td>
                                        <td className="px-4 py-3 text-right font-mono font-medium">${r.balance.toFixed(2)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-[#F4F1EA]"><td className="px-4 py-3" colSpan={2}><strong>Total Expense</strong></td><td className="px-4 py-3 text-right font-mono font-bold" data-testid="pl-total-expense">${data.total_expense.toFixed(2)}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-[#F2D8D5]/40 border border-[#E53935]/20 rounded-2xl p-5 flex justify-between items-center" data-testid="pl-net">
                        <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Net change in net assets</p>
                        <p className={`font-heading text-3xl font-medium ${data.net_change_in_assets >= 0 ? "text-green-700" : "text-red-700"}`}>${data.net_change_in_assets.toFixed(2)}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

// -------------------- Balance Sheet --------------------
const BalanceSheet = () => {
    const [asOf, setAsOf] = useState("");
    const [data, setData] = useState(null);
    useEffect(() => {
        const params = {};
        if (asOf) params.as_of = asOf;
        api.get("/books/reports/balance-sheet", { params }).then((r) => setData(r.data));
    }, [asOf]);

    const renderSection = (title, rows, color, testid) => (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <div className={`px-5 py-3 ${color}`}><p className="text-xs uppercase tracking-wider font-bold">{title}</p></div>
            <table className="w-full text-sm">
                <tbody className="divide-y divide-black/5">
                    {rows.filter((r) => r.balance !== 0).map((r) => (
                        <tr key={r.code} data-testid={`${testid}-${r.code}`}>
                            <td className="px-4 py-3 font-mono text-xs text-[#4A5568] w-20">{r.code}</td>
                            <td className="px-4 py-3 text-[#1C252E]">{r.name}</td>
                            <td className="px-4 py-3 text-right font-mono">${r.balance.toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div data-testid="books-bs">
            <DateRange asOf={asOf} setAsOf={setAsOf} oneDate testid="bs-range" />
            {data && (
                <div className="space-y-4">
                    {renderSection("Assets", data.assets, "bg-blue-50 text-blue-800 border-b border-blue-100", "bs-asset")}
                    {renderSection("Liabilities", data.liabilities, "bg-amber-50 text-amber-800 border-b border-amber-100", "bs-liab")}
                    {renderSection("Net Assets / Equity", data.equity, "bg-purple-50 text-purple-800 border-b border-purple-100", "bs-eq")}

                    <div className="bg-[#FDFBF7] border border-black/10 rounded-2xl p-5 space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-[#4A5568]">Total assets</span><strong className="font-mono">${data.total_assets.toFixed(2)}</strong></div>
                        <div className="flex justify-between"><span className="text-[#4A5568]">Total liabilities</span><strong className="font-mono">${data.total_liabilities.toFixed(2)}</strong></div>
                        <div className="flex justify-between"><span className="text-[#4A5568]">Current period net (P&L close-out preview)</span><strong className="font-mono">${data.current_period_net.toFixed(2)}</strong></div>
                        <div className="flex justify-between pt-2 border-t border-black/10"><span className="text-[#1C252E] font-medium">Total net assets</span><strong className="font-mono">${data.total_net_assets.toFixed(2)}</strong></div>
                        <div className={`text-xs ${data.balanced ? "text-green-700" : "text-red-700"} text-right`} data-testid="bs-balanced-flag">{data.balanced ? "Balance sheet balances ✓" : "Out of balance — investigate!"}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

// -------------------- Functional Expense --------------------
const FunctionalExpense = () => {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [data, setData] = useState(null);
    useEffect(() => {
        const params = {};
        if (start) params.start = start;
        if (end) params.end = end;
        api.get("/books/reports/functional-expense", { params }).then((r) => setData(r.data));
    }, [start, end]);
    return (
        <div data-testid="books-fx">
            <DateRange start={start} end={end} setStart={setStart} setEnd={setEnd} testid="fx-range" />
            {data && (
                <>
                    <p className="text-xs text-[#4A5568] mb-4">Required for IRS Form 990 — breaks total expenses into Program / Management / Fundraising buckets.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {["Program", "Management", "Fundraising", "Uncategorized"].map((b) => (
                            <div key={b} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`fx-bucket-${b.toLowerCase()}`}>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mb-1">{b}</p>
                                <p className="font-heading text-2xl text-[#1C252E] font-medium">${(data.totals[b] || 0).toFixed(2)}</p>
                                <p className="text-[11px] text-[#4A5568] mt-1">{(data.percentages[b] || 0).toFixed(1)}% of total</p>
                            </div>
                        ))}
                    </div>
                    {Object.entries(data.buckets).map(([bucket, rows]) => rows.length > 0 && (
                        <div key={bucket} className="bg-white border border-black/5 rounded-2xl mb-3 overflow-hidden">
                            <div className="px-5 py-3 bg-[#F4F1EA] text-xs uppercase tracking-wider font-bold text-[#4A5568]">{bucket}</div>
                            <table className="w-full text-sm">
                                <tbody className="divide-y divide-black/5">
                                    {rows.map((r) => (
                                        <tr key={r.code}>
                                            <td className="px-4 py-2 font-mono text-xs text-[#4A5568] w-20">{r.code}</td>
                                            <td className="px-4 py-2 text-[#1C252E]">{r.name}</td>
                                            <td className="px-4 py-2 text-right font-mono">${r.balance.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

// -------------------- Donors --------------------
const Donors = () => {
    const [donors, setDonors] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    useEffect(() => { api.get("/books/donors").then((r) => setDonors(r.data)); }, []);
    return (
        <div data-testid="books-donors">
            <div className="flex justify-between items-end mb-4">
                <p className="text-sm text-[#4A5568]">{donors.length} unified donors · auto-created from donations</p>
                <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Acknowledgment year</label>
                    <input type="number" min={2020} max={2100} value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="w-28 rounded-lg border border-black/10 px-3 py-2 text-sm" data-testid="donor-letter-year" />
                </div>
            </div>
            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Donor</th><th className="text-left px-4 py-3 font-medium">Type</th><th className="text-left px-4 py-3 font-medium">Email</th><th className="text-right px-4 py-3 font-medium">Lifetime</th><th className="text-right px-4 py-3 font-medium">Gifts</th><th className="text-right px-4 py-3"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {donors.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-[#4A5568]">No donors yet.</td></tr>}
                        {donors.map((d) => (
                            <tr key={d.id} data-testid={`donor-row-${d.id}`}>
                                <td className="px-4 py-3"><p className="font-medium text-[#1C252E]">{d.name}</p>{d.anonymous_public && <span className="text-[10px] text-[#4A5568]">(anonymous on public wall)</span>}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{d.donor_type}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{d.email || "—"}</td>
                                <td className="px-4 py-3 text-right font-mono font-medium text-[#1C252E]">${d.total_donated.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right text-[#4A5568]">{d.donation_count}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <a href={`${API}/books/donor-letter/${d.id}.pdf?year=${year}`} target="_blank" rel="noreferrer" data-testid={`donor-letter-btn-${d.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-[#E53935] hover:underline">
                                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Letter
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// -------------------- Customers --------------------
const Customers = () => {
    const [customers, setCustomers] = useState([]);
    useEffect(() => { api.get("/books/customers").then((r) => setCustomers(r.data)); }, []);
    return (
        <div data-testid="books-customers">
            <p className="text-sm text-[#4A5568] mb-4">{customers.length} unified customers · auto-created from paid merch orders</p>
            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Customer</th><th className="text-left px-4 py-3 font-medium">Email</th><th className="text-right px-4 py-3 font-medium">Lifetime spent</th><th className="text-right px-4 py-3 font-medium">Orders</th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {customers.length === 0 && <tr><td colSpan={4} className="px-6 py-12 text-center text-[#4A5568]">No customers yet.</td></tr>}
                        {customers.map((c) => (
                            <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                                <td className="px-4 py-3 font-medium text-[#1C252E]">{c.name}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{c.email || "—"}</td>
                                <td className="px-4 py-3 text-right font-mono font-medium">${c.total_spent.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right text-[#4A5568]">{c.order_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// -------------------- Audit & Reconcile --------------------
const AuditReconcile = () => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reconciling, setReconciling] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/financials/audit");
            setReport(data);
        } catch (e) {
            toast.error("Could not load audit");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const reconcile = async () => {
        if (!window.confirm("Backfill missing journal entries for every paid donation? This is safe to run repeatedly — already-posted donations are skipped.")) return;
        setReconciling(true);
        try {
            const { data } = await api.post("/admin/financials/reconcile");
            toast.success(`Reconciled: ${data.posted} new JE(s) posted, ${data.skipped_already_posted} already current`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reconcile failed");
        } finally {
            setReconciling(false);
        }
    };

    if (loading && !report) return <p className="text-sm text-[#4A5568]">Running audit…</p>;
    if (!report) return null;

    const s = report.summary || {};
    const hasIssues = (report.donations_without_je?.length || 0) + (report.duplicates?.length || 0) + (report.restricted_misposted?.length || 0) + (report.journal_entries_without_donation?.length || 0) > 0;

    return (
        <div className="space-y-6" data-testid="audit-panel">
            {/* Summary card */}
            <div className={`border rounded-2xl p-5 ${s.balanced ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-300"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <p className="font-heading text-xl text-[#1C252E] flex items-center gap-2">
                            {s.balanced ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <AlertTriangle className="w-5 h-5 text-amber-700" />}
                            {s.balanced ? "Books balanced" : "Books out of balance"}
                        </p>
                        <p className="text-sm text-[#4A5568] mt-1">
                            {s.donations_count} paid donation(s) · ${s.donations_total?.toFixed(2)} collected · ${s.ledger_contribution_income?.toFixed(2)} posted to GL
                            {!s.balanced && <span className="font-bold text-amber-800"> · drift ${Math.abs(s.drift_donations_minus_ledger || 0).toFixed(2)}</span>}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={load} className="px-4 py-2 rounded-full text-sm border border-black/10 hover:border-[#E53935]" data-testid="audit-refresh-btn">
                            <RefreshCw className="w-4 h-4 inline -mt-0.5 mr-1" />
                            Refresh
                        </button>
                        {hasIssues && (
                            <button onClick={reconcile} disabled={reconciling} className="px-4 py-2 rounded-full text-sm font-medium bg-[#E53935] hover:bg-[#D32F2F] text-white disabled:opacity-60" data-testid="audit-reconcile-btn">
                                <ShieldCheck className="w-4 h-4 inline -mt-0.5 mr-1" />
                                {reconciling ? "Reconciling…" : "Backfill missing JEs"}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Donations without JE */}
            {report.donations_without_je?.length > 0 && (
                <div className="bg-white border border-amber-200 rounded-2xl overflow-hidden" data-testid="audit-missing-je">
                    <div className="bg-amber-50 px-5 py-3 border-b border-amber-200">
                        <p className="font-medium text-amber-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> {report.donations_without_je.length} paid donation(s) missing a journal entry
                        </p>
                        <p className="text-xs text-amber-800 mt-0.5">Click "Backfill missing JEs" above to post all of these to the GL with the correct restricted / unrestricted account.</p>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-4 py-2">Donor</th>
                                <th className="text-left px-4 py-2">Amount</th>
                                <th className="text-left px-4 py-2">Source</th>
                                <th className="text-left px-4 py-2">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {report.donations_without_je.map((d) => (
                                <tr key={d.id} data-testid={`audit-missing-row-${d.id}`}>
                                    <td className="px-4 py-2">{d.donor_name || "Anonymous"} <span className="text-xs text-[#4A5568] block">{d.donor_email}</span></td>
                                    <td className="px-4 py-2 font-mono">${Number(d.amount || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2 text-xs">{d.source || "—"}</td>
                                    <td className="px-4 py-2 text-xs text-[#4A5568]">{(d.created_at || "").slice(0, 10)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Duplicate donations */}
            {report.duplicates?.length > 0 && (
                <div className="bg-white border border-red-200 rounded-2xl overflow-hidden" data-testid="audit-duplicates">
                    <div className="bg-red-50 px-5 py-3 border-b border-red-200">
                        <p className="font-medium text-[#E53935] flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> {report.duplicates.length} suspected duplicate donation group(s)
                        </p>
                        <p className="text-xs text-red-700 mt-0.5">Open the Donations tab and delete the duplicate rows. Deleting a donation auto-voids its JE.</p>
                    </div>
                    <div className="divide-y divide-black/5">
                        {report.duplicates.map((g, i) => (
                            <div key={i} className="px-5 py-3 text-sm" data-testid={`audit-dup-${i}`}>
                                <p className="text-xs uppercase tracking-wider text-red-700 font-bold">{g.kind} · {g.key}</p>
                                {g.rows.map((r) => (
                                    <div key={r.id} className="ml-2 text-xs text-[#4A5568] mt-1">
                                        · <span className="font-mono">{r.id.slice(0, 8)}</span> · {r.donor_name || "Anonymous"} · ${Number(r.amount).toFixed(2)} · {(r.created_at || "").slice(0, 19).replace("T", " ")}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Restricted misposted */}
            {report.restricted_misposted?.length > 0 && (
                <div className="bg-white border border-amber-200 rounded-2xl overflow-hidden">
                    <div className="bg-amber-50 px-5 py-3 border-b border-amber-200">
                        <p className="font-medium text-amber-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> {report.restricted_misposted.length} donation(s) posted to wrong restricted account
                        </p>
                        <p className="text-xs text-amber-800 mt-0.5">Use the "Re-post JE" button on the donation row to fix the account mapping.</p>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr><th className="text-left px-4 py-2">Donor</th><th className="text-left px-4 py-2">Campaign</th><th className="text-left px-4 py-2">Expected</th><th className="text-left px-4 py-2">Actual</th></tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {report.restricted_misposted.map((m, i) => (
                                <tr key={i}><td className="px-4 py-2">{m.donor_name}</td><td className="px-4 py-2">{m.campaign_name}</td><td className="px-4 py-2 font-mono text-green-700">{m.expected_account}</td><td className="px-4 py-2 font-mono text-[#E53935]">{m.actual_accounts.join(", ")}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Orphan JEs */}
            {report.journal_entries_without_donation?.length > 0 && (
                <div className="bg-white border border-amber-200 rounded-2xl p-5">
                    <p className="font-medium text-amber-900 flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4" /> {report.journal_entries_without_donation.length} journal entry(ies) reference deleted donations
                    </p>
                    <p className="text-xs text-amber-800 mb-3">These were created for donations that have since been removed. Open the Journal tab to void them.</p>
                    <ul className="text-xs text-[#4A5568] space-y-1">
                        {report.journal_entries_without_donation.map((j) => (
                            <li key={j.id}>· {j.entry_number} — {j.narration} (${j.total_debit?.toFixed(2)})</li>
                        ))}
                    </ul>
                </div>
            )}

            {!hasIssues && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center" data-testid="audit-clean">
                    <CheckCircle2 className="w-10 h-10 text-green-700 mx-auto mb-2" />
                    <p className="font-medium text-green-900">No discrepancies found</p>
                    <p className="text-sm text-green-800 mt-1">Every paid donation has a journal entry · books balanced · no duplicates · all restricted donations posted correctly.</p>
                </div>
            )}
        </div>
    );
};
